"use client";

import React from "react";
import { Calendar, User, Tag, ArrowRight, Share2 } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { AspectRatio } from "@/components/ui/aspect-ratio";


const formatDate = (dateString?: string) => {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

function SinglePostConsumer() {
  const { posts, loading, error, refetch } = usePosts();

  if (loading) {
    return (
      <div className="container mx-auto max-w-4xl px-6 py-24">
        <div className="space-y-8 animate-pulse">
          <div className="h-12 w-3/4 bg-muted rounded-md" />
          <div className="flex gap-4">
            <div className="h-6 w-32 bg-muted rounded-md" />
            <div className="h-6 w-32 bg-muted rounded-md" />
          </div>
          <div className="aspect-video w-full bg-muted rounded-xl" />
          <div className="space-y-4">
            <div className="h-4 w-full bg-muted rounded-md" />
            <div className="h-4 w-full bg-muted rounded-md" />
            <div className="h-4 w-2/3 bg-muted rounded-md" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto flex min-h-[50vh] flex-col items-center justify-center px-6 text-center">
        <h2 className="text-2xl font-serif font-bold text-destructive">Unable to load post</h2>
        <p className="mt-2 text-muted-foreground">Something went wrong while fetching the content.</p>
        <Button onClick={refetch} variant="outline" className="mt-6">
          Try Again
        </Button>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="container mx-auto flex min-h-[50vh] flex-col items-center justify-center px-6 text-center">
        <h2 className="text-2xl font-serif font-bold">Post not found</h2>
        <p className="mt-2 text-muted-foreground">The content you are looking for does not exist.</p>
        <div className="mt-6">
          <Link to="/HomePage">
            <Button>Return Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="container mx-auto max-w-4xl px-6 py-12 md:py-20">
      {}
      <header className="mb-10 text-center md:mb-14 md:text-left">
        <div className="mb-6 flex flex-wrap items-center justify-center gap-4 text-sm text-muted-foreground md:justify-start">
          {post.categories && post.categories.length > 0 && (
            <div className="flex items-center gap-1.5 text-primary font-medium">
              <Tag className="h-4 w-4" />
              <span>{post.categories[0].name}</span>
            </div>
          )}
          <Separator orientation="vertical" className="h-4 hidden md:block" />
          <div className="flex items-center gap-1.5">
            <Calendar className="h-4 w-4" />
            <time dateTime={post.date}>{formatDate(post.date)}</time>
          </div>
          <Separator orientation="vertical" className="h-4 hidden md:block" />
          <div className="flex items-center gap-1.5">
            <User className="h-4 w-4" />
            <span>{post.author?.name || "FilmFrame"}</span>
          </div>
        </div>

        <h1 className="font-serif text-4xl font-medium leading-tight tracking-tight text-foreground md:text-5xl lg:text-6xl">
          {post.title}
        </h1>
      </header>

      {}
      {post.featuredImage && (
        <div className="mb-12 overflow-hidden rounded-xl bg-muted shadow-sm md:mb-16">
          <AspectRatio ratio={16 / 9}>
            <img
              src={post.featuredImage}
              alt={post.title}
              className="h-full w-full object-cover"
            />
          </AspectRatio>
        </div>
      )}

      {}
      <div className="prose prose-neutral dark:prose-invert max-w-none md:prose-lg prose-headings:font-serif prose-headings:font-medium prose-a:text-primary prose-img:rounded-xl">
        {post.content ? (
          <div dangerouslySetInnerHTML={{ __html: post.content }} />
        ) : (
          <p className="text-muted-foreground italic">No content available for this post.</p>
        )}
      </div>

      <Separator className="my-12 md:my-16" />

      {}
      <div className="flex flex-col gap-8 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-4">
          <Avatar className="h-14 w-14 border border-border">
            <AvatarImage src={post.author?.avatar?.["96"]} alt={post.author?.name} />
            <AvatarFallback>
              {post.author?.name?.charAt(0) || "A"}
            </AvatarFallback>
          </Avatar>
          <div>
            <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Written by</p>
            <h3 className="font-serif text-xl font-medium text-foreground">
              {post.author?.name || "The Artist"}
            </h3>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" className="gap-2">
            <Share2 className="h-4 w-4" />
            Share Article
          </Button>
        </div>
      </div>

      {}
      <div className="mt-16 rounded-2xl bg-muted/50 p-8 text-center md:p-12">
        <h3 className="mb-4 font-serif text-2xl font-medium text-foreground md:text-3xl">
          Interested in this piece?
        </h3>
        <p className="mx-auto mb-8 max-w-xl text-muted-foreground">
          Many of the works featured in our journal are available as limited edition fine art prints. 
          Inquire about availability and sizing.
        </p>
        <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
          <Link to="/InquirePage">
            <Button size="lg" className="w-full sm:w-auto">
              Inquire Availability
            </Button>
          </Link>
          <Link to="/TheArchivesPage">
            <Button variant="outline" size="lg" className="w-full gap-2 sm:w-auto">
              View Archives <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    </article>
  );
}

export default function SinglePostTemplateSection() {
  
  const currentWPQueryParams = wvcClient.getCurrentWPQueryParams();
  
  
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section id="single-post-template-section" className="bg-background w-full">
      <PostsProvider wp_query={wp_query}>
        <SinglePostConsumer />
      </PostsProvider>
    </section>
  );
}